import { useEffect, useRef, useState } from "react";
import { Button, Card, Col, Form, Row } from "react-bootstrap";
import {
  getDoAndChargeSummary,
  createDoAndChargeInvoice,
  replaceDoAndChargeInvoice,
} from "../../services/apiClient.js";

// Do & Charge — roll the claim's un-invoiced contractor costs (+ markup / P&G / PM /
// rubbish / call-out) into a receivable Quoted Invoice AND an approved variation
// carrying the job's value. Legacy OrdersPage "Extra Charges & Mark-up" panel.
//
// Legacy rules: only shown for Do & Charge / Standard Go Ahead / Callout Emergency
// Works jobs (never legacy final-quote jobs); Generate needs something yet-to-invoice;
// Replace rebuilds the latest UNPAID Do & Charge invoice in place (same number),
// preloading the inputs it was generated with; the header text is required.

const nzd = new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD" });
const money = (v) => nzd.format(Number(v || 0));
const EMPTY = {
  markup_pct: "", pg_pct: "", pm_hours: "", pm_rate: "", rubbish_loads: "", rubbish_rate: "",
  callout_hours: "", callout_rate: "", header: "", markup_as_line: false, separate_lines: true,
};

function fromStored(opts) {
  if (!opts || typeof opts !== "object") return EMPTY;
  return { ...EMPTY, ...Object.fromEntries(Object.entries(opts).filter(([k]) => k in EMPTY)) };
}

// The PRESETS (Order Fee Profiles, System Data): rates and the checkbox
// defaults for this job's segment. Quantities (hours, loads) and the header
// stay blank on purpose — they are the person's (KS 2026-08-26: "auto-fill
// the RATES, leave the QUANTITIES to the user"). A blank rate = no preset.
const str = (v) => (v == null ? "" : String(v));
function fromDefaults(d) {
  return {
    ...EMPTY,
    markup_pct: str(d.markup_pct),
    pg_pct: str(d.pg_pct),
    pm_rate: str(d.pm_rate),
    rubbish_rate: str(d.rubbish_rate),
    callout_rate: str(d.callout_rate),
    separate_lines: d.separate_lines !== false,
    markup_as_line: d.markup_as_line === true,
  };
}

function DoAndChargePanel({ claimId, onChanged }) {
  const [summary, setSummary] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  const [headerInvalid, setHeaderInvalid] = useState(false);
  const headerRef = useRef(null);

  function load(preload = false) {
    return getDoAndChargeSummary(claimId)
      .then((s) => {
        setSummary(s);
        // An unpaid invoice's own inputs WIN over the presets: Replace must
        // rebuild the document from what it was generated with, not from
        // whatever the profile says today.
        if (preload) {
          if (s.unpaid?.do_and_charge) setForm(fromStored(s.unpaid.do_and_charge));
          else if (s.defaults) setForm(fromDefaults(s.defaults));
        }
      })
      .catch(() => setError("Unable to load do & charge summary."));
  }
  useEffect(() => {
    load(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claimId]);

  const set = (patch) => setForm((c) => ({ ...c, ...patch }));

  async function run(action, label) {
    if (!form.header.trim()) {
      // Point AT the field, don't just announce it (Karen/Robert 2026-07-15:
      // the banner alone read as "there's no way to enter one").
      setError("Please enter the Invoice/Quote Header (required).");
      setHeaderInvalid(true);
      headerRef.current?.focus();
      return;
    }
    setHeaderInvalid(false);
    setBusy(true);
    setError("");
    setMsg("");
    try {
      const inv = await action();
      setMsg(`Invoice #${inv.invoice_no} ${label} (${money(inv.total)}) — see the Invoices tab.`);
      await load();
      if (onChanged) onChanged();
    } catch (err) {
      setError(err.response?.data?.error || "Unable to generate invoice.");
    } finally {
      setBusy(false);
    }
  }
  const generate = () => run(() => createDoAndChargeInvoice(claimId, form), "created");
  const replace = () =>
    run(() => replaceDoAndChargeInvoice(claimId, summary.unpaid.id, form), "replaced");

  const numField = (label, key, width = 6) => (
    <Col xs={width} md={3}>
      <Form.Label className="mb-0 small fw-semibold">{label}</Form.Label>
      <Form.Control size="sm" type="number" step="any" value={form[key]} onChange={(e) => set({ [key]: e.target.value })} />
    </Col>
  );

  // Legacy rule: the panel only exists for the D&C-style job types.
  if (summary && !summary.eligible) return null;

  const canGenerate = Number(summary?.yet_to_invoice) > 0;
  const canReplace = !!summary?.unpaid;

  return (
    <Card className="mt-4">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <span className="fw-semibold">Do &amp; Charge — Generate Invoice</span>
        {summary ? (
          <span className="small">
            Contractor invoices — Already invoiced: <strong>{money(summary.already_invoiced)}</strong> ·
            Yet to be invoiced: <strong>{money(summary.yet_to_invoice)}</strong>
          </span>
        ) : null}
      </Card.Header>
      <Card.Body>
        {error ? <div className="alert alert-danger py-2">{error}</div> : null}
        {msg ? <div className="alert alert-success py-2">{msg}</div> : null}

        {!canGenerate && !canReplace ? (
          <div className="text-muted small">
            No new contractor invoices received. Add contractor invoices on the orders above first.
          </div>
        ) : (
          <>
            {/* Where the numbers came from, said out loud — the quote grid's
                pricing-profile line, Orders-page clothing. */}
            {summary?.defaults ? (
              <div className="small text-muted mb-2">
                {summary.defaults.profiled && !summary.defaults.standard
                  ? <>Order fee profile <strong>{summary.defaults.group}</strong> prefilled the rates</>
                  : <>Standard order rates prefilled</>}
                {" — hours and loads are typed per job. "}
                <Button
                  variant="link" size="sm" className="p-0 align-baseline"
                  data-hint="Put the profile's rates and checkbox defaults back, keeping the header"
                  onClick={() => setForm((f) => ({ ...fromDefaults(summary.defaults), header: f.header }))}
                >
                  Reset rates to profile
                </Button>
              </div>
            ) : null}
            <Row className="g-2 mb-2">
              {numField("Markup %", "markup_pct")}
              {numField("P&G / Overheads %", "pg_pct")}
              {numField("PM hours", "pm_hours")}
              {numField("PM rate", "pm_rate")}
              {numField("Rubbish loads", "rubbish_loads")}
              {numField("Rubbish rate", "rubbish_rate")}
              {numField("Call-out hours", "callout_hours")}
              {numField("Call-out rate", "callout_rate")}
            </Row>
            <Row className="g-2 align-items-end">
              <Col xs={12} md={6}>
                {/* A red * rather than the word "(required)", which sat in the
                    same small grey label as everything else and did not stand out
                    (Karen, 16 Aug). Same marker ClaimForm uses, so it is a
                    convention people learn once instead of a one-off. */}
                <Form.Label className="mb-0 small fw-semibold">
                  Invoice/Quote header<span className="text-danger"> *</span>
                </Form.Label>
                <Form.Control
                  size="sm"
                  ref={headerRef}
                  isInvalid={headerInvalid}
                  value={form.header}
                  placeholder="e.g. Do & Charge repairs"
                  onChange={(e) => {
                    set({ header: e.target.value });
                    if (e.target.value.trim()) setHeaderInvalid(false);
                  }}
                />
              </Col>
              <Col xs={12} md={6} className="d-flex align-items-center gap-3 flex-wrap">
                <Form.Check type="checkbox" id="dc-sep" label="Separate line per contractor invoice" checked={form.separate_lines} onChange={(e) => set({ separate_lines: e.target.checked })} />
                <Form.Check type="checkbox" id="dc-markupline" label="Markup as its own line" checked={form.markup_as_line} onChange={(e) => set({ markup_as_line: e.target.checked })} />
              </Col>
            </Row>
            <div className="mt-3 d-flex gap-2">
              {canGenerate ? (
                <Button size="sm" onClick={generate} disabled={busy}>
                  {busy ? "Working…" : "Generate Invoice"}
                </Button>
              ) : null}
              {canReplace ? (
                <Button size="sm" variant="outline-warning" onClick={replace} disabled={busy}>
                  {busy ? "Working…" : `Replace Variations for Invoice ${summary.unpaid.invoice_no}`}
                </Button>
              ) : null}
            </div>
          </>
        )}
      </Card.Body>
    </Card>
  );
}

export default DoAndChargePanel;
